import type { DevicePresence } from "@facade/dashboard-shared";

/**
 * The two parts of a device card that answer "which install is this, and which build".
 * Extracted from Devices.tsx as pure functions of a DevicePresence so they can be asserted
 * directly: the merge note and the absent-sha state render nowhere a human can currently look
 * (production serves the pre-merge card, and no local row is a split pair), so a rendering test
 * is the only check either one has.
 */

const MERGE_TITLE =
  "An older client enrolled under the bare device id and reported telemetry under the suffixed one, " +
  "leaving two stored records for one install. Shown as one row; revoking it clears both.";

const NO_SHA_TITLE = "This install predates build stamping, so it cannot be tied to a commit.";

/** Renders only when a row stands for more than one stored record — silence is the common case. */
export function MergeNote({ device }: { device: DevicePresence }) {
  const n = device.mergedRecords ?? 1;
  if (n <= 1) return null;
  return (
    <div className="muted" style={{ fontSize: 11 }} title={MERGE_TITLE}>
      one install · {n} stored records (older enrolment folded in)
    </div>
  );
}

/**
 * versionName alone cannot identify a build — every R1 install reports the same string — so the
 * sha is the discriminator that makes a tester's report attributable. Absent is a REAL,
 * distinguishable state, so it says so rather than rendering an empty cell that reads as correct.
 */
export function BuildIdentity({ device }: { device: DevicePresence }) {
  return (
    <>
      {device.info?.app?.versionName ?? "?"}
      {" · "}
      {device.gitSha ? (
        <code>{device.gitSha}</code>
      ) : (
        <span className="muted" title={NO_SHA_TITLE}>
          no build id
        </span>
      )}
      {device.variantExp && (
        <>
          {" · "}
          <span className="chip">
            {device.variantExp}/{device.variantId}
          </span>
        </>
      )}
    </>
  );
}
